import { createContext, useCallback, useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import TransitionPortal from '@/components/Common/TransitionPortal';
import useColor from '@/hooks/domain/useColor';
import useKeymap from '@/hooks/pure/useKeymap';
import { timeOut } from '@/utils/util';

import Button from '../Button/Button';
import {
  ModalBody,
  ModalDesc,
  ModalFooter,
  ModalHeader,
  ModalLayout,
  ModalTitle,
} from './styles';

export const modalContext = createContext<{
  closeModal: () => Promise<void>;
}>({
  closeModal: () => Promise.resolve(),
});

type Props = {
  children: React.ReactNode;
  status: boolean;
  closeModal: () => void;
  layerId?: string;
  useBackgroundClose?: boolean;
  useEscapeClose?: boolean;
  modalZIndex?: number;
  backgroundStyle?: React.CSSProperties;
};

export const useModalContext = () => {
  return useContext(modalContext);
};

const duration = 200;

const Modal = (props: Props) => {
  const color = useColor();
  const { status, closeModal } = props;

  useKeymap(
    'Escape',
    status && props.useEscapeClose ? closeModal : undefined,
  );

  const asyncCloseModal = useCallback(() => {
    const promise = new Promise<void>((resolve) => {
      closeModal();
      void timeOut(duration).then(() => {
        resolve();
      });
    });
    return promise;
  }, [closeModal]);

  return (
    <TransitionPortal
      status={status}
      layerId={props.layerId || 'modal-layer'}
      close={closeModal}
      background={{
        enable: props.useBackgroundClose,
        style: {
          backgroundColor: color.FD20,
          backdropFilter: 'blur(10px)',
          zIndex: props.modalZIndex ? props.modalZIndex - 1 : undefined,
          ...props.backgroundStyle,
        },
      }}
      duration={duration}
      onClick={props.useBackgroundClose ? closeModal : undefined}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 'none',
        background: 'transparent',
        backdropFilter: 'none',
        zIndex: props.modalZIndex ? props.modalZIndex : undefined,
      }}
    >
      <ErrorBoundary
        fallbackRender={(e) => (
          <Modal.Layout modalSize="s">
            <Modal.Header>
              <Modal.Title>{e.error.message}</Modal.Title>
              <Modal.Desc>Please try again later.</Modal.Desc>
            </Modal.Header>
            <Modal.Footer>
              <Button
                variant="primary"
                sizeType="m"
                onClick={() => closeModal()}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal.Layout>
        )}
      >
        <modalContext.Provider value={{ closeModal: asyncCloseModal }}>
          <div onClick={(e) => e.stopPropagation()}>{props.children}</div>
        </modalContext.Provider>
      </ErrorBoundary>
    </TransitionPortal>
  );
};

Modal.Layout = ModalLayout;
Modal.Header = ModalHeader;
Modal.Footer = ModalFooter;
Modal.Body = ModalBody;
Modal.Title = ModalTitle;
Modal.Desc = ModalDesc;

export default Modal;
